import { fetchUserAttributes } from 'aws-amplify/auth';
import { AxiosResponse } from 'axios';
import { defineStore } from 'pinia';

import api from '@/api/api';
import {
  employmentStatusOptions,
  maritalStatusOptions,
  residentialStatusOptions,
  titleOptions,
} from '@/constants/constants';
import { BROKER_SESSION_URL, IDENTIFY_URL, IS_VALID_PROFILE_URL, PROFILE_URL } from '@/constants/urls';
import { LOG_PREFIX } from '@/types/enums';
import {
  Address,
  AddressInfo,
  BrokerSessionAddress,
  BrokerSessionCardForm,
  BrokerSessionResponse,
  PersonalDetails,
  Profile,
} from '@/types/profile';

const normaliseSessionValue = (value?: string): string => {
  return value?.toUpperCase().replace(/[^A-Z0-9]+/g, '_').replace(/^_|_$/g, '') || '';
};

const brokerValueAliases: Record<string, string> = {
  LIVING_WITH_PARTNER: 'LIVING_WITH_MY_PARTNER',
};

const getOptionId = (value: string | undefined, options: Array<{ id: number; label: string }>): number | string => {
  const normalisedValue = normaliseSessionValue(value);
  const optionValue = brokerValueAliases[normalisedValue] || normalisedValue;

  return options.find((option) => normaliseSessionValue(option.label) === optionValue)?.id || '';
};

const getOptionLabel = (value: string | undefined, options: string[]): string => {
  return options.find((option) => normaliseSessionValue(option) === normaliseSessionValue(value)) || '';
};

const mapAddress = (address: BrokerSessionAddress): Address => {
  return {
    countryCode: address.countryCode || '',
    postcode: address.postcode || '',
    county: address.county || '',
    postTown: address.postTown || '',
    thoroughfare: address.thoroughfare || '',
    label: address.label || '',
    ...(address.buildingNumber && { buildingNumber: address.buildingNumber }),
    ...(address.buildingName && { buildingName: address.buildingName }),
    ...(address.subBuildingName && { subBuildingName: address.subBuildingName }),
    ...(address.organisation && { organisation: address.organisation }),
    ...(address.udprn && { udprn: address.udprn }),
    ...(address.addressProviderId && { addressProviderId: address.addressProviderId }),
    ...(address.dependentLocality && { dependentLocality: address.dependentLocality }),
  };
};

const mapCardFormToProfile = (cardForm: BrokerSessionCardForm): { personalDetails: PersonalDetails; addressList: AddressInfo[] } => {
  const employmentStatusId = getOptionId(cardForm.employmentStatus, employmentStatusOptions);

  return {
    personalDetails: {
      id: '',
      firstName: cardForm.firstName || '',
      lastName: cardForm.lastName || '',
      titleId: getOptionId(cardForm.title, titleOptions),
      dateOfBirth: cardForm.dateOfBirth?.slice(0, 10) || '',
      maritalStatusId: getOptionId(cardForm.maritalStatus, maritalStatusOptions),
      phoneNumber: '',
      numberOfDependants: cardForm.numberOfDependants ?? '',
      dependantExpenditure: cardForm.dependantExpenditure ?? '',
      residentialStatus: getOptionLabel(cardForm.residentialStatus, residentialStatusOptions),
      monthlyAccommodationCosts: cardForm.monthlyAccommodationCosts ?? '',
      otherHouseIncome: cardForm.otherHouseIncome ?? '',
      email: cardForm.email || '',
      employment: cardForm.employmentStatus
        ? [{
          employmentStatusId,
          annualIncome: cardForm.annualIncome ?? '',
          mainEmployment: true,
        }]
        : [],
    },
    addressList: [cardForm.address, cardForm.previousAddress]
      .filter((address): address is BrokerSessionAddress => Boolean(address))
      .map((address) => ({ address: mapAddress(address) })),
  };
};

export default defineStore('profile', {
  state: () => ({
    accountId: null as string | null,
    personalDetails: null as PersonalDetails | null,
    addressList: [] as AddressInfo[] | [],
    hasUnsavedChanges: false,
    isValidProfile: null as boolean | null,
  }),
  actions: {
    async initialiseFromBrokerSession(): Promise<boolean> {
      const response = await api({
        url: BROKER_SESSION_URL,
        method: 'get',
        logPrefix: LOG_PREFIX.PROFILE,
      }) as AxiosResponse<BrokerSessionResponse>;
      const cardForm = response.data?.session?.state?.creditCards?.cardForm;

if (!cardForm || Object.keys(cardForm).length === 0) return false;

      const { personalDetails, addressList } = mapCardFormToProfile(cardForm);
      this.personalDetails = personalDetails;
      this.addressList = addressList;

      return true;
    },

    async getProfile(): Promise<void> {
      const response = await api({
        url: PROFILE_URL,
        method: 'get',
        logPrefix: LOG_PREFIX.PROFILE,
      }) as AxiosResponse<Profile>;

      if (response.data) {
        this.accountId = response.data.accountId ?? null;
        this.personalDetails = response.data.personalDetails;
        this.addressList = response.data.addressList || [];
      }
    },

    async updateProfile(data: Profile): Promise<void> {
      await api({
        url: PROFILE_URL,
        method: 'post',
        data,
        logPrefix: LOG_PREFIX.PROFILE,
      });
    },

    async checkIsValidProfile(): Promise<void> {
      const response = await api({
        url: IS_VALID_PROFILE_URL,
        method: 'get',
        logPrefix: LOG_PREFIX.IS_VALID_PROFILE,
      }) as AxiosResponse<boolean>;

      this.isValidProfile = response.data;
    },

    async creditProfileIdentify(): Promise<AxiosResponse> {
      return api({
        url: IDENTIFY_URL,
        method: 'post',
        data: {
          blackBox: window.IGLOO.getBlackbox().blackbox,
        },
        logPrefix: LOG_PREFIX.IDENTIFY_PROFILE,
      });
    },
  }
});
